import * as ko from 'knockout';

import { ListRequestParams, getContentRange } from '../api/request';
import { BaseLoadingScreen } from './base_loading_screen';
import {
  ListLoaderDelegate,
  ListLoader,
  ItemSelectionTracker,
  delegateActions,
  confirmBulkDelete,
} from '../components/list_loader';
import { formatAttributes } from '../models/attribute_meta';
import { AttributeFilters, DimensionMeta } from '../models/dimension_meta';
import { Crop } from '../models/crop';
import * as cropsApi from '../api/crops';
import * as dimensionMetasApi from '../api/dimension_metas';
import * as namesApi from '../api/names';
import i18n from '../i18n';
import { translate } from '../i18n_text';
import { ListHeaderAction } from '../components/list_header';
import { Action } from '../components/basic_widgets';
import { DimensionRecordActions } from './dimension_records';
import { canCreateCrop, canDeleteCrop, canEditCrops, reactListTablesEnabled } from '../permissions';
import { session } from '../session';
import { asArray, updateLocationWithQueryString } from '../utils';
import { FilterDelegate } from '../components/list_filters';
import { deflateList } from '../api/serialization';
import { removeDialog } from '../components/remove_dialog';
import { ShowArchivedFilter } from '../components/show_archived_filter';
import { GridView, refreshRows, pageParams } from '../utils/grid_view';
import { GridChoices, listLookup } from '../utils/grid_choices';
import { ARCHIVED_HIDDEN_FILTER } from '@/lib/gridViewState';
import {
  CropsListChoices,
  CropsListColumn,
  CropsListPage,
  CropsListProps,
  CropsListQuery,
  CropsListRow,
  IslandGridSelection,
} from 'react-islands';

let template = require('raw-loader!../../templates/crops.html').default;

interface DefaultFilters {
  name_search?: string;
  anonymized_code?: string;
  crop_category_ids?: string | string[];
}

type CropsFilters = DefaultFilters & AttributeFilters;

class CropsScreen extends BaseLoadingScreen implements ListLoaderDelegate<cropsApi.CropData, Crop> {
  loader: ListLoader<cropsApi.CropData, Crop>;
  showArchivedFilter: ShowArchivedFilter;
  dimensionRecordActions: DimensionRecordActions;

  canEdit = canEditCrops();
  canCreate = canCreateCrop();
  canDelete = canDeleteCrop();
  canViewDetails = session.isFieldtrials();

  dimensionMeta = ko.observable<DimensionMeta>(null);

  listActions = ko.observableArray<ListHeaderAction>([]);

  private nameFilter = ko.observable('').extend({ throttle: 300 });
  private anonymizedCodeFilter = ko.observable('').extend({ throttle: 300 });
  private categoryFilter = ko.observableArray<namesApi.NameI18nData>();
  searchPlaceholder = i18n.t('Search crops')();

  private listCropCategories = (params: ListRequestParams) => namesApi.list('crop_categories', params);

  newFilters: FilterDelegate[] = [
    {
      title: i18n.t('Anonymized code')(),
      textValue: this.anonymizedCodeFilter,
    },
    {
      title: i18n.t('Category')(),
      entities: this.categoryFilter,
      list: this.listCropCategories,
    },
  ];

  allowItemsPerLoadDisplay = true;
  allowEditFilters = true;

  selectionTracker: ItemSelectionTracker<Crop>;
  selectedIds = ko.pureComputed(() => this.selectionTracker.selectedItems().map((c) => c.id()));
  bulkRemoveTooltipTitle = i18n.t('Delete selected crops')();

  newCropsTable = reactListTablesEnabled();

  private tableColumns: CropsListColumn[] = [];

  private view = new GridView(
    'crops',
    new GridChoices<CropsListChoices>(
      {
        category: listLookup((params) => namesApi.list('crop_categories', params), 'category'),
      },
      () => ({
        category: [],
      })
    )
  );

  tableProps = ko.pureComputed<CropsListProps>(() => ({
    view: this.view,
    fetchPage: this.fetchPage,
    columns: this.tableColumns,
    canDelete: this.canDelete,
    onBulkDelete: this.onBulkDelete,
  }));

  private subscriptions: KnockoutSubscription[] = [];

  constructor(params: { filters: CropsFilters }) {
    super();

    this.nameFilter(params.filters.name_search || '');
    this.anonymizedCodeFilter(params.filters.anonymized_code || '');

    this.showArchivedFilter = new ShowArchivedFilter(dimensionMetasApi.CROP_SLUG, () => {
      if (this.loader) this.loader.forceLoad();
    });

    this.dimensionRecordActions = new DimensionRecordActions(() => this.canEdit, {
      onArchive: () => this.applyChange(),
      onUnarchive: () => this.applyChange(),
    });

    if (this.canCreate) {
      this.listActions.push({
        title: i18n.t('Add')(),
        icon: 'add_circle',
        href: '/crops/new/',
        tooltipTitle: i18n.t('Add crop')(),
      });
    }
    if (this.canEdit) {
      this.listActions.push({
        title: i18n.t('Customize')(),
        icon: 'settings',
        href: '/dimensions/crop/',
      });
      this.listActions.push({
        title: i18n.t('Import')(),
        icon: 'file_upload',
        href: '/dimensions/crop/import/',
        tooltipTitle: i18n.t('Import crops')(),
      });
    }

    let dmPromise = dimensionMetasApi.retrieve(dimensionMetasApi.CROP_SLUG);
    let promise = Promise.all([this.listCropCategories({}), dmPromise]).then(
      ([cropCategoriesData, dmData]) => {
        this.dimensionMeta(new DimensionMeta(dmData));
        if (this.newCropsTable) {
          this.tableColumns = this.dimensionMeta().attributeColumns();
        } else {
          this.newFilters.push(...this.dimensionMeta().getAttributeFilters(params.filters));
        }

        if (params.filters.crop_category_ids) {
          this.categoryFilter(
            cropCategoriesData.filter((cat) => asArray(params.filters.crop_category_ids).includes(cat.id))
          );
        }
      }
    );
    this.loadedAfter(
      this.newCropsTable
        ? promise.then(() =>
            this.view.load(params.filters, { firstVisit: { filter: ARCHIVED_HIDDEN_FILTER } })
          )
        : promise
    );
  }

  onReady(loader: ListLoader<cropsApi.CropData, Crop>) {
    this.loader = loader;
    this.selectionTracker = new ItemSelectionTracker(this.loader.items);
    this.subscriptions.push(this.nameFilter.subscribe(() => this.loader.forceLoad()));
  }

  onRefresh(crops: Crop[]): void {
    const selectedIds = new Set(this.selectedIds());
    const updatedSelected = crops.filter((c) => selectedIds.has(c.id()));
    this.selectionTracker.selectedItems(updatedSelected);
  }

  fetch(params: ListRequestParams) {
    const filters = this.getFilters();
    updateLocationWithQueryString(filters);
    return cropsApi.list({ ...params, ...filters, withAdditionalData: true }, undefined, {
      injectTenant: true,
      onHeadersReceived: (headers) => {
        this.selectionTracker.total(getContentRange(headers).total);
      },
    });
  }

  private getFilters() {
    return {
      name_search: this.nameFilter(),
      anonymized_code: this.anonymizedCodeFilter(),
      crop_category_ids: deflateList(this.categoryFilter),
      ...this.dimensionMeta().getAttributeFilterValuesBySlug(),
      ...(this.showArchivedFilter.on() ? { archived: null } : { archived: false }),
    };
  }

  instantiate(data: cropsApi.CropData) {
    return new Crop(data, this.dimensionMeta());
  }

  getEditUrl(entity: Crop) {
    return this.canEdit && entity.editUrl();
  }

  remove(id: string) {
    return cropsApi.remove(id);
  }

  canRemove(crop: Crop) {
    return this.canDelete;
  }

  confirmBulkRemove = () => {
    removeDialog(
      i18n.t('Crops')().toLowerCase(),
      [],
      () =>
        cropsApi.bulkRemove({
          ...this.getFilters(),
          ...(this.selectionTracker.allSelected() ? {} : { ids: this.selectedIds() }),
          delete_limit: this.selectionTracker.selectedCount(),
        }),
      this.selectionTracker.selectedCount()
    )
      .then(() => {
        this.loader.refresh();
        this.selectionTracker.clear();
      })
      .catch(() => {});
  };

  private applyChange = () => refreshRows(this.newCropsTable, this.view, this.loader);

  private fetchPage = (query: CropsListQuery): Promise<CropsListPage> =>
    this.view.fetchPage(
      query,
      (options) =>
        cropsApi.list(
          // Archived is a filter condition; `archived: null` overrides the client's default of hiding it.
          { ...pageParams(query, 'name_search'), withAdditionalData: true, archived: null },
          undefined,
          options
        ),
      this
    );

  toRow = (crop: Crop): CropsListRow => ({
    id: crop.id(),
    name: translate(crop.nameJson()),
    // Empty when users may not open a crop: the name cell then renders plain text.
    url: this.canViewDetails ? session.toTenantPath(crop.editUrl()) : '',
    anonymizedCode: crop.anonymizedCode() || '',
    categoryName: crop.category() ? translate(crop.category().name_json) : '',
    archived: !!crop.archived(),
    values: formatAttributes(crop.attributes()),
    actions: delegateActions(this, crop, this.view.reload),
  });

  private onBulkDelete = (selection: IslandGridSelection) =>
    confirmBulkDelete(
      i18n.t('Crops')().toLowerCase(),
      selection,
      (scope) => cropsApi.bulkRemove({ ...this.view.queryParams('name_search'), ...scope }),
      this.view.reload
    );

  getActions(crop: Crop): Action[] {
    return this.dimensionRecordActions.getAll({
      id: crop.id(),
      dimensionMetaNameSlug: this.dimensionMeta().nameSlug(),
      archived: crop.archived(),
    });
  }

  dispose() {
    this.subscriptions.forEach((s) => s.dispose());
    this.subscriptions = [];
  }
}

export let crops = {
  name: 'crops',
  viewModel: CropsScreen,
  template: template,
};

ko.components.register(crops.name, crops);
